// A period as a button naming its dates; open, it takes a from and a to by
// hand or one of the screen's usual periods in a click. Shared by the sales
// board and the deals screen's changes view, which each offer their own
// shortcuts.

import { useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./PeriodPicker.module.css";
import {
  periodEnd,
  samePeriod,
  type Period,
  type PeriodShortcut,
} from "../pages/sales/salesModel.js";
import { formatDateRange, isCalendarDate } from "../format/calendarDate.js";

export function PeriodPicker({
  period,
  today,
  shortcuts,
  onChange,
}: {
  period: Period;
  today: string;
  shortcuts: readonly PeriodShortcut[];
  onChange: (period: Period) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const end = periodEnd(period, today);
  const named = shortcuts.find((shortcut) => samePeriod(shortcut.period, period));

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent | KeyboardEvent) => {
      if (
        event instanceof KeyboardEvent
          ? event.key === "Escape"
          : !box.current?.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  return (
    <div className={styles.period} ref={box}>
      <button
        type="button"
        className={styles.control}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        {named === undefined ? formatDateRange(period.from, end) : named.label}
        <span className={styles.periodDates}>
          {named === undefined ? "" : formatDateRange(period.from, end)}
        </span>
      </button>
      {open && (
        <div className={styles.periodMenu} role="dialog" aria-label="Period">
          <div className={styles.shortcuts}>
            {shortcuts.map((shortcut) => (
              <button
                key={shortcut.key}
                type="button"
                className={`${styles.shortcut} ${shortcut === named ? styles.shortcutActive : ""}`}
                onClick={() => {
                  onChange(shortcut.period);
                  setOpen(false);
                }}
              >
                {shortcut.label}
              </button>
            ))}
          </div>
          <div className={styles.range}>
            <label className={styles.rangeField}>
              From
              <input
                type="date"
                className={styles.control}
                value={period.from}
                max={end}
                onChange={(event) => {
                  const from = event.target.value;
                  if (isCalendarDate(from) && from <= end) onChange({ from, to: period.to });
                }}
              />
            </label>
            <label className={styles.rangeField}>
              To
              <input
                type="date"
                className={styles.control}
                value={end}
                min={period.from}
                max={today}
                onChange={(event) => {
                  const to = event.target.value;
                  if (isCalendarDate(to) && to >= period.from && to <= today) {
                    onChange({ from: period.from, to: to === today ? null : to });
                  }
                }}
              />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}
